import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/axios'

// Ordem e colunas escondidas de uma lista, gravadas por usuario no servidor
// (rota /leiautes/:tela, migration 021). Mudar (arrastar/esconder/restaurar) aparece
// na hora, mas so fica gravado ao clicar em "Gravar leiaute" (gravar()).
// Colunas novas que o leiaute salvo nao conhece entram no fim; as que deixaram de
// existir sao ignoradas.

interface Leiaute { ordem: string[]; ocultas: string[] }

const igual = (a: Leiaute, b: Leiaute) =>
  a.ordem.join('|') === b.ordem.join('|') && [...a.ocultas].sort().join('|') === [...b.ocultas].sort().join('|')

export function useLeiauteColunas(tela: string, padrao: string[]) {
  const [leiaute, setLeiaute] = useState<Leiaute>({ ordem: padrao, ocultas: [] })
  const [gravado, setGravado] = useState<Leiaute>({ ordem: padrao, ocultas: [] })
  const [carregado, setCarregado] = useState(false)
  const [gravando, setGravando] = useState(false)
  const [arrastando, setArrastando] = useState<string | null>(null)
  const chavePadrao = padrao.join('|')

  useEffect(() => {
    let ativo = true
    setCarregado(false)
    api.get<{ data: Leiaute | null }>(`/leiautes/${tela}`)
      .then(r => {
        if (!ativo || !r.data.data?.ordem) return
        const l = { ordem: r.data.data.ordem, ocultas: r.data.data.ocultas ?? [] }
        setLeiaute(l)
        setGravado(l)
      })
      .catch(() => { /* sem leiaute salvo: fica o padrao */ })
      .finally(() => { if (ativo) setCarregado(true) })
    return () => { ativo = false }
  }, [tela])

  const colunas = useMemo(() => {
    const validas = leiaute.ordem.filter(id => padrao.includes(id))
    return [...validas, ...padrao.filter(id => !validas.includes(id))]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leiaute.ordem, chavePadrao])

  // move a coluna `id` para a posicao da coluna `alvo`
  function mover(id: string, alvo: string) {
    if (id === alvo || !padrao.includes(id)) return
    const ordem = colunas.filter(c => c !== id)
    const pos = ordem.indexOf(alvo)
    const daEsquerda = colunas.indexOf(id) < colunas.indexOf(alvo)
    ordem.splice(daEsquerda ? pos + 1 : pos, 0, id)
    setLeiaute({ ordem, ocultas: leiaute.ocultas })
  }

  function alternar(id: string) {
    const ocultas = leiaute.ocultas.includes(id) ? leiaute.ocultas.filter(o => o !== id) : [...leiaute.ocultas, id]
    if (ocultas.length >= padrao.length) return // pelo menos uma coluna visivel
    setLeiaute({ ordem: colunas, ocultas })
  }

  function restaurar() {
    setLeiaute({ ordem: padrao, ocultas: [] })
  }

  // grava no servidor; igual ao padrao = apaga o leiaute salvo
  async function gravar(): Promise<boolean> {
    const atual = { ordem: colunas, ocultas: leiaute.ocultas.filter(o => padrao.includes(o)) }
    const ehPadrao = igual(atual, { ordem: padrao, ocultas: [] })
    setGravando(true)
    try {
      await api.put(`/leiautes/${tela}`, { leiaute: ehPadrao ? null : atual })
      setGravado(atual)
      return true
    } catch {
      return false
    } finally { setGravando(false) }
  }

  const atualVisto = { ordem: colunas, ocultas: leiaute.ocultas }
  const gravadoVisto = { ordem: [...gravado.ordem.filter(id => padrao.includes(id)), ...padrao.filter(id => !gravado.ordem.includes(id))], ocultas: gravado.ocultas }

  return {
    colunas,
    visiveis: colunas.filter(id => !leiaute.ocultas.includes(id)),
    ocultas: leiaute.ocultas,
    carregado,
    naoGravado: carregado && !igual(atualVisto, gravadoVisto),
    gravando,
    arrastando,
    setArrastando,
    mover,
    alternar,
    restaurar,
    gravar,
  }
}

export type LeiauteColunas = ReturnType<typeof useLeiauteColunas>
